import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Container,
  Title,
  Text,
  Paper,
  Group,
  Button,
  ActionIcon,
  Badge,
  Stack,
  Modal,
  Code,
  CopyButton,
  Alert,
  LoadingOverlay,
  Box,
  Card,
  Grid,
  Divider,
  SimpleGrid,
  Anchor,
  Breadcrumbs,
  Menu,
  Image,
  ThemeIcon,
  ScrollArea,
} from "@mantine/core";
import {
  IconEye,
  IconCopy,
  IconTrash,
  IconCheck,
  IconForms,
  IconExternalLink,
  IconRefresh,
  IconPlus,
  IconArrowLeft,
  IconUser,
  IconMail,
  IconMapPin,
  IconTextSize,
  IconPhone,
  IconLink,
  IconCalendar,
  IconNumbers,
  IconHome,
  IconSettings,
  IconChevronDown,
  IconDotsVertical,
  IconArrowAutofitDown,
  IconArrowDown,
} from "@tabler/icons-react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  query,
  orderBy,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { BenchTable } from "../pages/UI";
import { ROUTES } from "../../routes/routesPaths";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchStatCard from "../UIComponent/Components/BenchStatCard";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import BenchTopHeader from "../UIComponent/Components/BenchTopHeader";
import formbuildericon from "../../assets/Formbulidericons.svg";
import { FcExpand } from "react-icons/fc";
import { tr } from "date-fns/locale";
import { EmbedSnippet } from "./FormBuilder";
// Types
type FieldType =
  | "text"
  | "email"
  | "textarea"
  | "select"
  | "number"
  | "tel"
  | "url"
  | "date";

interface Validation {
  required?: boolean;
  min_length?: number;
  max_length?: number;
  pattern?: string;
  custom_message?: string;
}

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  options?: string[];
  validation?: Validation;
}

interface CustomForm {
  id: string; // Firestore document ID
  form_id: string; // Our generated form ID
  title: string;
  send_to: string;
  description: string;
  fields: FormField[];
  created_by: string | null;
  created_at: Timestamp;
  is_active: boolean;
  submission_count?: number;
  form_type?: "dynamic" | "single_branch";
  branch_id?: string;
}

// Field Icon Component
const FieldIcon: React.FC<{ type: FieldType; size?: number }> = ({
  type,
  size = 16,
}) => {
  const getIcon = () => {
    switch (type) {
      case "text":
        return IconUser;
      case "email":
        return IconMail;
      case "textarea":
        return IconTextSize;
      case "select":
        return IconForms;
      case "number":
        return IconNumbers;
      case "tel":
        return IconPhone;
      case "url":
        return IconLink;
      case "date":
        return IconCalendar;
      default:
        return IconForms;
    }
  };

  const IconComponent = getIcon();
  return <IconComponent size={size} />;
};

// Form Details Modal Component
const FormDetailsModal: React.FC<{
  form: CustomForm | null;
  isOpen: boolean;
  onClose: () => void;
}> = ({ form, isOpen, onClose }) => {
  if (!form) return null;

  const embedSnippet = `<div id="form-container"></div>
<script src="${process.env.REACT_APP_CURRENT_DOMAIN_NAME}/FormBuilder/formWidgetJs?form_id=${form.form_id}"></script>`;

  const getFieldTypeDisplay = (type: FieldType): string => {
    switch (type) {
      case "text":
        return "Text";
      case "email":
        return "Email";
      case "textarea":
        return "Text Area";
      case "select":
        return "Dropdown";
      case "number":
        return "Number";
      case "tel":
        return "Phone";
      case "url":
        return "URL";
      case "date":
        return "Date";
      default:
        return type;
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Title size="lg">Form Details: {form.title}</Title>}
      size="xl"
    >
      <Stack>
        {/* Basic Information */}
        <Card withBorder>
          <Title order={4} mb="md">
            Basic Information
          </Title>
          <Grid>
            <Grid.Col span={6}>
              <Text fw={500}>Form ID:</Text>
              <Code>{form.form_id}</Code>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500}>Status:</Text>
              <Badge color={form.is_active ? "green" : "red"}>
                {form.is_active ? "Active" : "Inactive"}
              </Badge>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500}>Title:</Text>
              <Text>{form.title}</Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500}>Submissions:</Text>
              <Text>{form.submission_count || 0}</Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text fw={500}>Description:</Text>
              <Text>{form.description || "No description"}</Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500}>Created:</Text>
              <Text>{form.created_at?.toDate().toLocaleDateString()}</Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500}>Fields:</Text>
              <Text>{form.fields.length} fields</Text>
            </Grid.Col>
            <Grid.Col span={12}>
              <Text fw={500}>Send To:</Text>
              <Text>{form.send_to || "Not specified"}</Text>
            </Grid.Col>
          </Grid>
        </Card>

        {/* Fields Summary */}
        <Card withBorder>
          <Title order={4} mb="md">
            Fields Summary
          </Title>
          <Stack spacing="xs">
            {form.fields.map((field, index) => (
              <Paper key={field.id} p="sm" withBorder>
                <Group position="apart" align="flex-start">
                  <Group>
                    <FieldIcon type={field.type} size={20} />
                    <div>
                      <Text fw={500}>{field.label}</Text>
                      <Group spacing="xs" mt={4}>
                        <Badge variant="light" size="sm">
                          {getFieldTypeDisplay(field.type)}
                        </Badge>
                        {field.validation?.required && (
                          <Badge color="red" variant="light" size="sm">
                            Required
                          </Badge>
                        )}
                        {field.placeholder && (
                          <Text size="sm" c="dimmed">
                            Placeholder: "{field.placeholder}"
                          </Text>
                        )}
                      </Group>
                    </div>
                  </Group>
                  <Text size="sm" c="dimmed">
                    #{index + 1}
                  </Text>
                </Group>
                {field.type === "select" && field.options && (
                  <Box mt="xs">
                    <Text size="sm" fw={500}>
                      Options:
                    </Text>
                    <Group spacing="xs" mt={4}>
                      {field.options.map((option, idx) => (
                        <Badge key={idx} variant="outline" size="xs">
                          {option}
                        </Badge>
                      ))}
                    </Group>
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        </Card>

        {/* Embed Code */}
        <Card withBorder>
          <Title order={4} mb="md">
            Embed Code
          </Title>
          <Text size="sm" c="dimmed" mb="sm">
            Copy this code to embed the form on your website:
          </Text>
          <Code block style={{ whiteSpace: "pre-wrap", fontSize: "12px" }}>
            {embedSnippet}
          </Code>
          <CopyButton value={embedSnippet}>
            {({ copied, copy }) => (
              <Button
                fullWidth
                variant="light"
                color={copied ? "teal" : "blue"}
                onClick={copy}
                leftIcon={
                  copied ? <IconCheck size={16} /> : <IconCopy size={16} />
                }
                mt="md"
              >
                {copied ? "Copied!" : "Copy Embed Code"}
              </Button>
            )}
          </CopyButton>
        </Card>
      </Stack>
    </Modal>
  );
};

// Delete Confirmation Modal
const DeleteConfirmationModal: React.FC<{
  form: CustomForm | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isDeleting: boolean;
}> = ({ form, isOpen, onClose, onConfirm, isDeleting }) => {
  return (
    <Modal opened={isOpen} onClose={onClose} title="Delete Form" size="md">
      <Stack>
        <Alert color="red" title="Warning">
          Are you sure you want to delete the form "{form?.title}"? This action
          cannot be undone.
        </Alert>
        <Text size="sm" c="dimmed">
          This will permanently remove the form and all its configuration. Any
          embedded forms will stop working.
        </Text>
        <Group position="right" mt="md">
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button color="red" onClick={onConfirm} loading={isDeleting}>
            Delete Form
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// Forms Grid View Component
const FormsGridView: React.FC<{
  forms: CustomForm[];
  onViewDetails: (form: CustomForm) => void;
  onDelete: (form: CustomForm) => void;
}> = ({ forms, onViewDetails, onDelete }) => {
  return (
    <SimpleGrid cols={3} spacing="lg">
      {forms.map((form) => (
        <Card key={form.id} withBorder shadow="sm" radius="md" padding="lg">
          <Stack spacing="md">
            <div>
              <Group position="apart" align="flex-start" mb="xs">
                <Title order={4} lineClamp={1} style={{ flex: 1 }}>
                  {form.title}
                </Title>
                <Badge color={form.is_active ? "green" : "red"} size="sm">
                  {form.is_active ? "Active" : "Inactive"}
                </Badge>
              </Group>
              <Text size="sm" c="dimmed" lineClamp={2} mb="sm">
                {form.description || "No description"}
              </Text>
            </div>

            <Group spacing="xs">
              {form.send_to && <Badge>{form.send_to || ""}</Badge>}
              <Badge variant="light" size="sm">
                {form.fields.length}{" "}
                {form.fields.length === 1 ? "field" : "fields"}
              </Badge>
              <Badge variant="outline" size="sm">
                {form.submission_count || 0} submissions
              </Badge>
            </Group>

            <Group spacing={4}>
              {form.fields.slice(0, 3).map((field, index) => (
                <FieldIcon key={index} type={field.type} size={14} />
              ))}
              {form.fields.length > 3 && (
                <Text size="xs" c="dimmed">
                  +{form.fields.length - 3} more
                </Text>
              )}
            </Group>

            <Text size="sm" c="dimmed">
              Created: {form.created_at?.toDate().toLocaleDateString()}
            </Text>

            <Divider />

            <Group spacing="xs" grow>
              <Button
                variant="light"
                size="sm"
                onClick={() => onViewDetails(form)}
                leftIcon={<IconEye size={16} />}
              >
                View
              </Button>
              <Button
                variant="outline"
                size="sm"
                color="red"
                onClick={() => onDelete(form)}
                leftIcon={<IconTrash size={16} />}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Card>
      ))}
    </SimpleGrid>
  );
};

// Main Forms List Component
const FormsList: React.FC = () => {
  const navigate = useNavigate();
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );
  const [forms, setForms] = useState<CustomForm[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedForm, setSelectedForm] = useState<CustomForm | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState<CustomForm | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [viewMode, setViewMode] = useState<"table" | "grid">("table");
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [embedFormId, setEmbedFormId] = useState<string | null>(null);

  const fetchForms = async () => {
    try {
      setLoading(true);
      setError(null);

      const formsQuery = query(
        collection(db, "customforms"),
        where("company_id", "==", companyId),
        orderBy("created_at", "desc"),
      );

      const querySnapshot = await getDocs(formsQuery);
      const formsData: CustomForm[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        formsData.push({
          id: doc.id,
          form_id: data.form_id,
          title: data.title,
          send_to: data.send_to,
          description: data.description,
          fields: data.fields || [],
          created_by: data.created_by,
          created_at: data.created_at,
          is_active: data.is_active !== undefined ? data.is_active : true,
          submission_count: data.submission_count || 0,
          form_type: data.form_type || "dynamic",
          branch_id: data.branch_id,
        });
      });

      setForms(formsData);
    } catch (err) {
      console.error("Error fetching forms:", err);
      setError("Failed to load forms. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    fetchForms();
  }, [companyId]);

  // Handle form deletion
  const handleDeleteForm = async () => {
    if (!formToDelete) return;
    try {
      setDeleting(true);
      await deleteDoc(doc(db, "customforms", formToDelete.id));

      // Remove from local state
      setForms(forms.filter((form) => form.id !== formToDelete.id));
      setDeleteModalOpen(false);
      setFormToDelete(null);
    } catch (err) {
      console.error("Error deleting form:", err);
      setError("Failed to delete form. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  // Open details modal
  const openDetailsModal = (form: CustomForm) => {
    setSelectedForm(form);
    setDetailsModalOpen(true);
  };

  // Open delete confirmation modal
  const openDeleteModal = (form: CustomForm) => {
    setFormToDelete(form);
    setDeleteModalOpen(true);
  };

  // Navigate to form builder
  const navigateToBuilder = () => {
    navigate(ROUTES.CREATE_FORM_PAGE);
  };

  // Table rows
  const rows = forms.map((form) => (
    <tr
      key={form.id}
      onClick={() =>
        navigate(ROUTES.EDIT_FORM_PAGE.replace(":formId", form.form_id))
      }
      style={{ cursor: "pointer" }}
    >
      <td>
        <Group spacing="sm">
          <FieldIcon type={form.fields[0]?.type || "text"} />
          <Stack spacing={0}>
            <Text fw={500}>{form.title}</Text>
            <Text size="sm" c="dimmed" lineClamp={1}>
              {/* {form.description || "No description"} */}
            </Text>
          </Stack>
        </Group>
      </td>
      {/* <td>{form.send_to && <Badge>{form.send_to || ""}</Badge>}</td> */}
      <td>
        <Code>{form.form_id}</Code>
      </td>
      <td>
        <Group spacing={4}>
          <Badge variant="light">
            {form.fields.length} {form.fields.length === 1 ? "field" : "fields"}
          </Badge>
          <Badge variant="outline" color={form.is_active ? "green" : "red"}>
            {form.is_active ? "Active" : "Inactive"}
          </Badge>
          {form.form_type === "single_branch" && (
            <Badge variant="filled" color="grape">
              Single Branch
            </Badge>
          )}
        </Group>
      </td>
      <td>
        <Badge variant="light" color="blue">
          {form.submission_count || 0} submissions
        </Badge>
      </td>
      <td>
        <Text size="sm">{form.created_at?.toDate().toLocaleDateString()}</Text>
        <Text size="xs" c="dimmed">
          {form.created_at?.toDate().toLocaleTimeString()}
        </Text>
      </td>
      <td>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Menu shadow="md" width={180} withinPortal>
            <Menu.Target>
              <ActionIcon variant="default">
                <FcExpand size={16} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                icon={<IconLink size={14} />}
                onClick={() => {
                  setEmbedFormId(form.form_id);
                  setIsEmbedModalOpen(true);
                }}
              >
                Share & Embed Code
              </Menu.Item>

              <CopyButton
                value={`${process.env.REACT_APP_CURRENT_DOMAIN_NAME}/FormBuilder/formWidgetJs?form_id=${form.form_id}`}
              >
                {({ copied, copy }) => (
                  <Menu.Item
                    icon={
                      copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                    }
                    onClick={copy}
                    color={copied ? "teal" : undefined}
                  >
                    {copied ? "Link Copied!" : "Copy Embed URL"}
                  </Menu.Item>
                )}
              </CopyButton>

              <Menu.Divider />

              <Menu.Item
                icon={<IconTrash size={14} />}
                color="red"
                onClick={() => openDeleteModal(form)}
              >
                Delete Form
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </td>
    </tr>
  ));
  const statsData = [
    {
      title: "Total Forms",
      count: forms.length,
      description: "All forms created",
      icon: <IconForms size={22} color="#1c7ed6" />,
      bgColor: "#e7f5ff",
      textColor: "#1c7ed6",
      descColor: "#5c7cfa",
    },
    {
      title: "Active Forms",
      count: forms.filter((f) => f.is_active).length,
      description: "Currently active forms",
      icon: <IconCheck size={22} color="#2b8a3e" />,
      bgColor: "#ebfbee",
      textColor: "#2b8a3e",
      descColor: "#40c057",
    },
    {
      title: "Total Submissions",
      count: forms.reduce(
        (total, form) => total + (form.submission_count || 0),
        0,
      ),
      description: "Total submissions across forms",
      icon: <IconNumbers size={22} color="#c2255c" />,
      bgColor: "#fff0f6",
      textColor: "#c2255c",
      descColor: "#d81b60",
    },
  ];

  const breadcrumbslinks = [{ label: "Form Builder" }];

  return (
    <div className="page">
      <Container size="lg">
        <LoadingOverlay visible={loading} />
        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 110, // Avoid overlapping the fixed navbar
            zIndex: 100,
            backgroundColor: "#FAFAFA",
            paddingTop: theme.spacing.sm,
            marginTop: `-${theme.spacing.sm}`,
          })}
        >
          <SimpleGrid cols={2}>
            <BenchBreadcrumbs links={breadcrumbslinks} />
            <Group position="right">
              <Button
                onClick={navigateToBuilder}
                leftIcon={<IconPlus size={16} />}
              >
                Create New Form
              </Button>

              <Menu shadow="md">
                <Menu.Target>
                  <Button
                    variant="outline"
                    rightIcon={<IconChevronDown size={16} />}
                  >
                    More Actions
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item>
                    <Button
                      variant={viewMode === "table" ? "filled" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("table")}
                    >
                      Table View
                    </Button>
                  </Menu.Item>
                  <Menu.Item>
                    <Button
                      variant={viewMode === "grid" ? "filled" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("grid")}
                    >
                      Grid View
                    </Button>
                  </Menu.Item>
                  <Menu.Item>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchForms}
                      leftIcon={<IconRefresh size={16} />}
                    >
                      Refresh
                    </Button>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </SimpleGrid>
          <Divider my="sm" />
        </Box>

        <BenchStatCard data={statsData} />

        {/* Error Alert */}
        {error && (
          <Alert color="red" mb="md">
            {error}
          </Alert>
        )}

        {/* Empty State */}
        {!loading && forms.length === 0 && (
          <Paper withBorder p="xl" ta="center">
            <Stack spacing="md">
              <IconForms size={64} color="var(--mantine-color-gray-5)" />
              <div>
                <Title order={4} mb="xs">
                  No Forms Created
                </Title>
                <Text c="dimmed">
                  You haven't created any forms yet. Start by creating your
                  first form.
                </Text>
              </div>
              <Button
                onClick={navigateToBuilder}
                leftIcon={<IconPlus size={16} />}
                size="sm"
                maw={300}
                mx="auto"
              >
                Create Your First Form
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Forms Table */}
        {!loading && forms.length > 0 && viewMode === "table" && (
          <ScrollArea>
            <BenchTable>
              <thead>
                <tr>
                  <th>Form Title</th>
                  <th>Form ID</th>
                  <th>Status & Fields</th>
                  <th>Submissions</th>
                  <th>Created</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>{rows}</tbody>
            </BenchTable>
          </ScrollArea>
        )}

        {/* Forms Grid */}
        {!loading && forms.length > 0 && viewMode === "grid" && (
          <FormsGridView
            forms={forms}
            onViewDetails={openDetailsModal}
            onDelete={openDeleteModal}
          />
        )}

        {/* Modals */}
        <FormDetailsModal
          form={selectedForm}
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
        />

        <DeleteConfirmationModal
          form={formToDelete}
          isOpen={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={handleDeleteForm}
          isDeleting={deleting}
        />

        <Modal
          opened={isEmbedModalOpen}
          onClose={() => setIsEmbedModalOpen(false)}
          title={<Title order={4}>Share & Embed Code</Title>}
          size="xl"
        >
          {embedFormId && <EmbedSnippet formId={embedFormId} />}
        </Modal>
      </Container>
    </div>
  );
};

export default FormsList;
